<script setup lang="ts">
import { isBedroomArea } from '~/helpers/house/bedrooms'
import type {
  HouseAreaEquipment,
  HouseKeyFeatures,
  HouseDetailsAttributes,
  HouseIncludedService,
  HouseSlug,
  HouseTruthLink,
  Locale,
  HousePhotoAttributes,
} from 'lc-services/types'
import type { HouseReview } from '~/types/house/types'

const { locale } = useI18n<unknown, Locale>()
const { isDesktopOrTablet } = useBreakpoint()

const {
  areaEquipments,
  featuredEquipments = { featured: [], equipments_count: 0 },
  getReviews,
  house,
  matterportVideoUrl = '',
  page = 'House',
} = defineProps<{
  areaEquipments: HouseAreaEquipment[]
  featuredEquipments: HouseKeyFeatures
  house: HouseDetailsAttributes
  houseDestinations: HouseDetailsAttributes['destination']
  houseIncludedServices: HouseIncludedService[]
  housePhotos: Array<HousePhotoAttributes & { hash: string }>
  houseTruthLinks: Pick<HouseTruthLink, 'description'>[]
  getGps: {
    latitude: number
    longitude: number
  }
  getReviews: Array<HouseReview & { quote: HouseSlug }>
  matterportVideoUrl: string
  page?: 'House' | 'Transaction'
  totalCount: number
}>()

defineEmits<{
  'open-modal-virtual-visit': []
}>()

const bedrooms = computed(() =>
  areaEquipments.flatMap((item) => item.areas.filter(isBedroomArea)),
)

const reviews = computed(() => {
  if (getReviews.length === 0) return []

  return getReviews
    .map((review) => {
      return {
        ...review,
        quote: review.quote[locale.value],
      }
    })
    .sort((a, b) => new Date(b.stay).getTime() - new Date(a.stay).getTime())
})
</script>

<template>
  <div
    class="house-informations relative w-full overflow-x-hidden pb-12 md:pb-16 lg:w-13/24 xl:w-14/24"
    :class="{ 'lc-col': isDesktopOrTablet }"
  >
    <div
      :class="[
        'flex justify-between text-md text-gray-600 xs:justify-start xs:gap-1 sm:block',
        house.description ? 'pb-5' : 'pb-10',
      ]"
    >
      <span>
        {{ house.capacity }}
        {{ $t('house.capacityKey', house.capacity) }}</span
      >
      <span class="px-1 md:px-2">·</span>
      <span>
        {{ house.bedrooms }}
        {{ $t('house.bedroomsKey', house.bedrooms) }}</span
      >
      <span class="px-1 md:px-2">·</span>
      <span>
        {{ house.bathrooms }}
        {{
          $t(
            isDesktopOrTablet
              ? 'house.bathroomsKeyDesktop'
              : 'house.bathroomsKey',
            house.bathrooms,
          )
        }}
      </span>
      <template v-if="house.surface">
        <span class="px-1 md:px-2">·</span>
        <span>{{ house.surface }} {{ $t('house.surface') }}</span>
      </template>
    </div>

    <HouseDescription
      class="whitespace-pre-wrap pb-10 md:mb-9 md:border-b md:border-gray-200"
      :quote="house.description"
    />

    <HouseKeyfeatures
      :area-equipments="areaEquipments"
      :class="[
        'pb-10',
        'md:mb-9',
        {
          'md:border-b md:border-gray-200': houseIncludedServices.length,
        },
      ]"
      :featured-equipments="featuredEquipments"
      :house="house"
    />

    <HouseIncludedServices
      class="pb-10 md:mb-9 md:border-b md:border-gray-200"
      :house-id="house.id"
      :house-included-services="houseIncludedServices"
    />

    <HouseRoomsPhotos
      v-if="bedrooms.length"
      class="fluid pb-9 pl-4 md:mb-9 md:mr-4"
      :bedrooms="bedrooms"
      :house="house"
    />

    <HouseVirtualVisit
      v-if="page === 'House' && matterportVideoUrl"
      :house="house"
      :matterport-video-url="matterportVideoUrl"
      @open-modal-virtual-visit="$emit('open-modal-virtual-visit')"
    />

    <HouseGalleryPhotos
      v-if="page === 'House'"
      class="pb-10 md:mb-9 md:border-b md:border-gray-200"
      :house="house"
      :house-photos="housePhotos"
      :total-count="totalCount"
    />

    <HouseTruthLinks
      v-if="houseTruthLinks?.length"
      class="pb-10 md:mb-9 md:border-b md:border-gray-200"
      :house-truth-links="houseTruthLinks"
    />

    <HouseConcierge
      v-if="page === 'House'"
      id="house-concierge"
      class="pb-10 md:mb-9 md:border-b md:border-gray-200"
      :concierge-service-offer="house.conciergeServiceOffer"
    />

    <HouseScheduleACall v-if="page === 'House'" class="mb-10" :house="house" />

    <HouseCustomerReviews
      v-if="reviews.length"
      class="pb-10 md:mb-9 md:border-b md:border-gray-200"
      :reviews="reviews"
    />

    <HouseAdditionalInformation
      v-if="page === 'House'"
      class="pb-10 md:mb-9 md:border-b md:border-gray-200"
      :house="house"
    />

    <HouseSurrounding
      class="pb-10"
      :house="house"
      :house-destinations="houseDestinations"
      :get-gps="getGps"
    />

    <HouseExperiences
      v-if="houseDestinations?.guide?.[locale]"
      :house="house"
      :guide-url="houseDestinations.guide[locale]"
    />

    <HouseBookingConditions v-if="page === 'House'" class="mb-5 pb-5" />
  </div>
</template>

<style scoped>
.house-informations > *:not(.fluid) {
  @apply mx-4;
}
</style>
